import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRadixConnect } from '../../radix_connect'; // Adjust path if needed
import { recordWithdrawal } from '../../backend'; // Adjust path if needed
import { supabase } from '../../backend/supabase';
import './MiningRigMenu.scss'; // Reuse styles or create SettingsMenu.scss

// Settings Menu Component
const SettingsMenu = ({ gameSave, onClose, refreshMoneyDisplay }) => {
  const navigate = useNavigate();
  const { rdt, connected, selectedAccount } = useRadixConnect();
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState(null);

  const balance = gameSave?.money || 0;
  const saveId = gameSave?.id; // Assuming gameSave has the DB record ID as 'id'
  // Get minimum withdrawal amount from game constants, default to 1000 if not available
  const MINIMUM_WITHDRAWAL_AMOUNT = gameSave?.constants?.MINIMUM_WITHDRAWAL_AMOUNT || 1000;

  const handleExitGame = () => {
    // Release pointer lock if active
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }

    // Small delay to ensure pointer lock is fully released before navigation
    setTimeout(() => {
      // Set skipRedirect flag to show company list when returning to StartScreen
      // Changed target to /start as per original code
      localStorage.setItem('skipRedirect', 'true');
      navigate('/start'); 
    }, 100);
  };

  const handleWithdraw = useCallback(async () => {
    // Add check for minimum amount using the constant from props
    if (!rdt || !connected || !selectedAccount || balance < MINIMUM_WITHDRAWAL_AMOUNT || !saveId || isWithdrawing) {
      console.warn('Withdraw conditions not met:', { rdt: !!rdt, connected, selectedAccount: !!selectedAccount, balance, saveId, isWithdrawing, minAmount: MINIMUM_WITHDRAWAL_AMOUNT });
      return;
    }

    setIsWithdrawing(true);
    setWithdrawError(null);
    setWithdrawSuccess(null);

    try {
      // Appel à la fonction serverless Supabase pour vérifier et préparer le retrait
      const { data: withdrawCheck, error: withdrawCheckError } = await supabase.functions.invoke('verify-withdrawal', {
        body: {
          saveId,
        }
      });

      if (withdrawCheckError) {
        throw new Error(withdrawCheckError.message || 'Failed to verify withdrawal');
      }

      if (!withdrawCheck.approved) {
        throw new Error(withdrawCheck.reason || 'Withdrawal not approved');
      }

      // Call backend to record the withdrawal and update balance to 0
      const updateResult = await recordWithdrawal(saveId, balance, withdrawCheck.transactionId);

      if (updateResult.error) {
        // Notify user that backend update failed, transaction might still succeed on chain
        setWithdrawError(`Transaction submitted, but backend update failed: ${updateResult.error.message || 'Unknown error'}`);
      } else {
        // Optionally clear the error if backend succeeds after submission error message
        if (withdrawError && withdrawError.startsWith('Transaction submitted')) {
          setWithdrawError(null);
        }
        setWithdrawSuccess(`Withdrawal successful! Transaction ID: ${withdrawCheck.transactionId.substring(0, 10)}...`);
        // Refresh the money display in the parent component
        if (refreshMoneyDisplay) {
          refreshMoneyDisplay();
        }
      }

    } catch (error) {
      // Catch errors from sendTransaction or other issues
      console.error('Withdrawal process error:', error);
      setWithdrawError(error.message || 'An unexpected error occurred during withdrawal.');
      // Clear success message if an error occurs
      setWithdrawSuccess(null); 
    } finally {
      setIsWithdrawing(false);
    }
  }, [rdt, connected, selectedAccount, balance, saveId, isWithdrawing, refreshMoneyDisplay, withdrawError]);


  return (
    <div className="settings-menu mining-rig-menu"> {/* Reuse existing styles */} 
      <div className="settings-content mining-rig-content"> {/* Reuse existing styles */} 
        <button className="close-button" onClick={onClose} aria-label="Close">×</button>
        <h2>Settings</h2>

        <div className="menu-options">
          {/* Existing Go Back Button */} 
          <button 
            className="menu-button"
            onClick={handleExitGame}
            disabled={isWithdrawing} // Disable while withdrawing
          >
            Go Back to Saves
          </button>

          {/* Withdraw Section */} 
          <div className="withdraw-section">
            <h4>Withdraw $REDDICKS</h4>
            <p>Current Balance: {balance.toLocaleString()} $REDDICKS</p>
            <button 
              className="menu-button withdraw-button" 
              onClick={handleWithdraw}
              // Update disabled condition using the constant from props
              disabled={balance < MINIMUM_WITHDRAWAL_AMOUNT || !connected || !selectedAccount || isWithdrawing || !saveId}
            >
              {isWithdrawing ? 'Withdrawing...' : `Withdraw All (${balance.toLocaleString()})`}
            </button>
            {!connected && <p className="error-message">Connect wallet to withdraw.</p>}
            {/* Add message for insufficient balance using the constant from props */}
            {connected && balance > 0 && balance < MINIMUM_WITHDRAWAL_AMOUNT && (
              <p className="info-message">Minimum withdrawal amount is {MINIMUM_WITHDRAWAL_AMOUNT.toLocaleString()} $REDDICKS.</p>
            )}
            {connected && balance <= 0 && <p className="info-message">No balance to withdraw.</p>}
            {!saveId && connected && <p className="error-message">Error: Cannot identify game save.</p>}
          </div>

          {/* Feedback Messages */} 
          {withdrawSuccess && <p className="success-message">{withdrawSuccess}</p>}
          {withdrawError && <p className="error-message">Error: {withdrawError}</p>}
        </div>

        <div className="credits">
          Reddicks Games
        </div>
      </div>
    </div>
  );
};

export default SettingsMenu; 